import {useCallback, useEffect, useMemo, useState} from 'react';
import {analytics} from './analytics';
import {setUser} from './analytics/sentry';
import {userManager} from './auth';
import {useUser} from './auth/use-auth';
import {
  NonProductionWarning,
  SnoozeNonProductionWarning,
  snoozeNonProductionWarningCacheKey,
} from './components/non-production-warning';
import {useSubscription} from './hooks/use-subscription';
import {PrimaryNavigation} from './navigation/primary-navigation';
import {LocationNotifier} from './routes/location-notifier';
import {useSignalR} from './signalr/use-signalr';
import {Outlet} from 'react-router-dom';
import {AppShell, AppShellHeaderConfiguration, Modal} from '@mantine/core';
import {useNotification} from './hooks/use-notifications';
import {useLocalStorage} from '@mantine/hooks';
import {useAsync} from 'react-use';
import {Env} from './config/env-vars.ts';

export const App = () => {
  const user = useUser();
  const [sessionExpired, setSessionExpired] = useState(false);
  const notifications = useNotification();

  useEffect(() => {
    analytics.identify(`${user.id}`);
    setUser(user);
  }, [user]);

  useAsync(async () => {
    await userManager.clearStaleState();
  }, []);

  useSubscription('session-expired', async () => {
    setSessionExpired(true);
    await userManager.signoutRedirect();
  });

  useSignalR(
    'TestSignal',
    useCallback(() => {
      notifications.info('TestSignal');
    }, [notifications]),
  );

  const onLocationChange = useCallback(
    (location: string) => analytics.trackPage(location),
    [],
  );

  const [snooze] = useLocalStorage<SnoozeNonProductionWarning>({
    key: snoozeNonProductionWarningCacheKey,
  });

  const headerProps = useMemo<AppShellHeaderConfiguration>(
    () =>
      Env.name === 'production'
        ? {height: 50}
        : {
            height: snooze?.hideProductionWarning ? 50 : 80,
          },
    [snooze?.hideProductionWarning],
  );

  return (
    <>
      <LocationNotifier onChange={onLocationChange} />

      <AppShell header={headerProps} offsetScrollbars={false}>
        <AppShell.Header>
          <NonProductionWarning />
          <PrimaryNavigation user={user} />
        </AppShell.Header>

        <AppShell.Main>
          <div className={'root-container'}>
            <div className='main-content'>
              <div className='content'>
                <Outlet />
              </div>
            </div>
          </div>
        </AppShell.Main>

        <Modal opened={sessionExpired} onClose={() => setSessionExpired(true)}>
          <Modal.Header>Session Expired</Modal.Header>
        </Modal>
      </AppShell>
    </>
  );
};
